// Casos de teste de um prompt ou agente: a lista contra a qual as versões são avaliadas.
import React, { useState } from "react";
import { Plus, Trash2, Upload } from "lucide-react";
import { api, invalidate, preview, toast, toastError, useQuery } from "../lib";
import { Badge, Button, Empty, Field, Modal } from "../ui";
import { CodeEditor } from "../editor";
import type { Case, CaseList } from "../../shared/types";

export function CasesSection({ p, t, varNames: specVars }: { p: string; t: string; varNames: string[] }) {
  const path = "/api/projects/" + p + "/targets/" + t + "/cases";
  const { data } = useQuery<CaseList>(path);
  const [edit, setEdit] = useState<Case | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const cases = data?.cases ?? [];
  const save = async (next: Case[]) => {
    try {
      await api.put(path, { cases: next });
      invalidate(path);
      invalidate("/api/projects/" + p + "/targets/" + t);
    } catch (e) {
      toastError(e);
    }
  };
  const varNames = [...new Set([...specVars, ...cases.flatMap((c) => Object.keys(c.vars ?? {}))])];
  const blank = (): Case => ({ id: "", vars: Object.fromEntries(varNames.map((n) => [n, ""])), expected: undefined, tags: [], notes: "" });
  return (
    <div className="section">
      <div className="section-head">
        <h2 className="section-title">Casos de teste{data ? <span className="faint" style={{ fontWeight: 400, marginLeft: 8 }}>{cases.length}</span> : null}</h2>
        <div className="row">
          <Button size="sm" icon={<Upload size={13} />} onClick={() => setImportOpen(true)}>Importar JSONL</Button>
          <Button size="sm" icon={<Plus size={13} />} onClick={() => setEdit(blank())}>Novo caso</Button>
        </div>
      </div>
      <div className="card">
        {!data ? (
          <div className="card-body"><div className="skeleton" style={{ height: 18, width: 220 }} /></div>
        ) : cases.length ? (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Id</th>
                  {varNames.map((n) => <th key={n}>{n}</th>)}
                  <th>Esperado</th>
                  <th>Notas</th>
                  <th style={{ width: 50 }} />
                </tr>
              </thead>
              <tbody>
                {cases.map((c, i) => (
                  <tr key={c.id} className="click" onClick={() => setEdit(c)}>
                    <td className="mono faint" style={{ fontSize: 12 }}>{c.id}</td>
                    {varNames.map((n) => <td key={n} style={{ maxWidth: 380 }}><div className="ellipsis">{c.vars?.[n] === undefined ? <span className="faint">—</span> : preview(c.vars[n], 110)}</div></td>)}
                    <td className="mono" style={{ fontSize: 12, maxWidth: 260 }}><div className="ellipsis">{c.expected === undefined ? <span className="faint">—</span> : preview(c.expected, 70)}</div></td>
                    <td style={{ maxWidth: 240 }}>
                      <div className="row" style={{ gap: 5 }}>{(c.tags ?? []).map((tag) => <Badge key={tag} tone={tag === "pegadinha" ? "amber" : tag === "capturado" ? "blue" : "plain"}>{tag}</Badge>)}</div>
                      {c.notes && <div className="faint ellipsis" style={{ fontSize: 12 }}>{c.notes}</div>}
                    </td>
                    <td className="r">
                      <Button size="xs" variant="ghost" icon={<Trash2 size={12} />} onClick={(e) => { e.stopPropagation(); save(cases.filter((_, j) => j !== i)); }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty title="Nenhum caso ainda" action={<Button icon={<Plus size={14} />} onClick={() => setEdit(blank())}>Adicionar caso</Button>}>
            Cada caso é uma entrada com o resultado esperado. Adicione à mão, importe um JSONL ou use "Virar caso" numa execução do Usage.
          </Empty>
        )}
      </div>
      {edit && (
        <CaseModal
          c={edit}
          varNames={varNames}
          onClose={() => setEdit(null)}
          onSave={(nc) => {
            const idx = edit.id ? cases.findIndex((x) => x.id === edit.id) : -1;
            const next = idx >= 0 ? cases.map((x, j) => (j === idx ? nc : x)) : [...cases, { ...nc, id: nc.id || "caso-" + (cases.length + 1) }];
            save(next);
            setEdit(null);
          }}
        />
      )}
      {importOpen && <ImportModal onClose={() => setImportOpen(false)} onImport={(more) => { save([...cases, ...more]); setImportOpen(false); toast(more.length + " casos importados"); }} />}
    </div>
  );
}

function CaseModal({ c, varNames, onClose, onSave }: { c: Case; varNames: string[]; onClose: () => void; onSave: (c: Case) => void }) {
  const [id, setId] = useState(c.id);
  const [vars, setVars] = useState(JSON.stringify(c.vars ?? {}, null, 2));
  const [expected, setExpected] = useState(c.expected === undefined ? "" : typeof c.expected === "string" ? JSON.stringify(c.expected) : JSON.stringify(c.expected, null, 2));
  const [notes, setNotes] = useState(c.notes ?? "");
  const [tags, setTags] = useState((c.tags ?? []).join(", "));
  const [err, setErr] = useState<string | null>(null);
  const submit = () => {
    try {
      const v = JSON.parse(vars || "{}");
      let e: any = undefined;
      if (expected.trim()) {
        try {
          e = JSON.parse(expected);
        } catch {
          e = expected;
        }
      }
      onSave({ id: id.trim(), vars: v, expected: e, notes, tags: tags.split(",").map((s) => s.trim()).filter(Boolean) });
    } catch (x: any) {
      setErr("Entradas: " + x.message);
    }
  };
  return (
    <Modal open onClose={onClose} title={c.id ? "Caso " + c.id : "Novo caso"} width={680} footer={<><span className="grow" style={{ color: "var(--red)", fontSize: 12 }}>{err}</span><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button variant="primary" onClick={submit}>Salvar</Button></>}>
      <div className="grid-2">
        <Field label="Id"><input className="input mono" value={id} onChange={(e) => setId(e.target.value)} placeholder="gerado se vazio" /></Field>
        <Field label="Tags"><input className="input" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="pegadinha, regressão" /></Field>
      </div>
      <Field label="Entradas (JSON)" hint={varNames.length ? "variáveis: " + varNames.join(", ") : undefined}><CodeEditor language="json" value={vars} onChange={setVars} minHeight={110} /></Field>
      <Field label="Esperado" hint="JSON ou texto. Os avaliadores comparam a saída com isto."><CodeEditor language="json" value={expected} onChange={setExpected} minHeight={70} /></Field>
      <Field label="Notas"><input className="input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="por que este caso existe" /></Field>
    </Modal>
  );
}

function ImportModal({ onClose, onImport }: { onClose: () => void; onImport: (c: Case[]) => void }) {
  const [text, setText] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const NL = String.fromCharCode(10);
  const submit = () => {
    try {
      const cases = text.split(NL).filter((l) => l.trim()).map((l) => {
        const o = JSON.parse(l);
        return o.vars ? o : { vars: o.input ?? o.inputs ?? o, expected: o.expected };
      });
      onImport(cases);
    } catch (e: any) {
      setErr(e.message);
    }
  };
  return (
    <Modal open onClose={onClose} title="Importar casos (JSONL)" width={680} footer={<><span className="grow" style={{ color: "var(--red)", fontSize: 12 }}>{err}</span><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button variant="primary" onClick={submit} disabled={!text.trim()}>Importar</Button></>}>
      <div className="field-hint">Um caso por linha, no formato {'{"id": "...", "vars": {...}, "expected": ...}'}.</div>
      <CodeEditor language="json" value={text} onChange={setText} minHeight={220} numbers />
    </Modal>
  );
}

